// external
import { writable } from 'svelte/store';
import { formatCurrency, getApiHost, getPotentialGAMClickTracker } from '../../../utils/index.js';
import { CartType, RetailerPartnerType } from '../enum/index.js';
// services
import checkoutServices, { initInstacartService } from '../services/index.js';
// endpoints
import { cartEndpoint } from '../data/endpoints.js';
let state = {
    type: CartType.DEFAULT,
    retailer: RetailerPartnerType.NONE,
    carts: [],
    currentCart: 0,
    currentTotal: ''
};
const store = writable(state);
/**
 * `init` initializes the store with additional
 * ICartState properties.
 * @param {ICartState} config - Partial<{@link ICartState}> with additional properties to override default state
 */
const init = (config) => {
    state = {
        ...state,
        ...config
    };
    if (state.carts.length > 0) {
        state.currentTotal = getCurrentCartTotal();
    }
    if (state.retailer === RetailerPartnerType.INSTACART) {
        initInstacartService();
    }
    store.set(state);
};
/**
 * `setValue` is a generic method used to update a single
 * key on the state, e.g. store.set<'retailer'>('retailer', RetailerType.TARGET);
 * @param key a property of the {@link ICartState} interface
 * @param value a valid value for the property
 */
const setValue = (key, value) => {
    state[key] = value;
    if (key === 'carts') {
        state.currentTotal = getCurrentCartTotal();
    }
    store.set(state);
};
/**
 * `toggleIsItemSelected` sets the isSelected property of
 * a cart item to its opposite value.
 * @param {number} sectionIndex - the index of the current section containing the item to toggle
 * @param {number} itemIndex - the index of the collection to toggle
 */
const toggleIsItemSelected = (sectionIndex, itemIndex) => {
    if (itemIndex > state.carts[state.currentCart].cartSections[sectionIndex].itemCollections.length) {
        console.warn(`WARNING: index ${itemIndex} in section ${sectionIndex} out of range for current cart collections`);
        return;
    }
    const collection = state.carts[state.currentCart].cartSections[sectionIndex].itemCollections[itemIndex];
    // Toggle isSelected property
    collection.isSelected = !collection.isSelected;
    state.carts[state.currentCart].cartSections[sectionIndex].itemCollections[itemIndex] = collection;
    state.currentTotal = getCurrentCartTotal();
    store.set(state);
};
/**
 * `changeItemForProduct` changes the current cart item for a cart
 * collection based on indices. example: swap UI call to change product
 * @param {string} productId - the id of the product (collection)
 * @param {number} selectedItemIndex - the index of the newly selected item
 * @param {number} cartSectionIndex - the index of the section containing the collection
 */
const changeItemForProduct = (productId, selectedItemIndex, cartSectionIndex) => {
    const collectionIndex = state.carts[state.currentCart].cartSections[cartSectionIndex].itemCollections
        .findIndex((collection) => collection.productId === productId);
    if (collectionIndex === -1) {
        console.warn(`WARNING: collection for product ID ${productId} not found`);
        return;
    }
    const collection = state.carts[state.currentCart].cartSections[cartSectionIndex].itemCollections[collectionIndex];
    if (selectedItemIndex > collection.items.length - 1) {
        console.warn(`WARNING: item index ${selectedItemIndex} out of range`);
        return;
    }
    // Move old item to the back of the array.
    const oldItem = collection.items.splice(collection.currentItem, 1)[0];
    collection.items.push(oldItem);
    // Because we have changed the array by splicing out 1 item, the (new item) itemIndex needs to shift by 1
    collection.currentItem = selectedItemIndex - 1;
    state.carts[state.currentCart].cartSections[cartSectionIndex].itemCollections[collectionIndex] = collection;
    state.currentTotal = getCurrentCartTotal();
    store.set(state);
};
/**
 * `getCurrentCartTotal` is used to give the total cost
 * of all selected items currently in the active cart
 */
const getCurrentCartTotal = () => {
    const cart = state.carts[state.currentCart];
    if (!cart)
        return formatCurrency(0);
    const total = cart.cartSections
        .flatMap((section) => section.itemCollections)
        .filter((collection) => collection.isSelected)
        .reduce((total, collection) => {
        const currentItem = collection.items[collection.currentItem];
        if (!currentItem)
            return total;
        const priceValue = currentItem.price;
        if (priceValue === undefined)
            return total;
        const price = parseFloat(priceValue);
        if (price)
            total += price;
        return total;
    }, 0);
    return formatCurrency(total);
};
/**
 * `buildQSParamsUrl` creates the query string param URL that includes the ids of the selected items when type is CartType.QA_PARAMS
 * @returns string
 */
const buildQSParamsUrl = () => {
    let url = state.checkoutUrl;
    if (!state.qsParam && url) {
        console.warn(`WARNING: state does not have a defined qsParam`);
        return url;
    }
    const selected = getAllItemCollectionsFromCartSections().filter((collection) => collection.isSelected);
    const params = selected.map((collection) => collection.items[collection.currentItem].id);
    if (url?.indexOf('?') === -1) {
        url += '?';
    }
    const lastChar = url?.charAt(url.length - 1);
    const mid = lastChar === '?' || lastChar === '&' ? '' : '&';
    return `${url}${mid}${state.qsParam}=${params.join(',')}`;
};
/**
 * `checkout` triggers the retailer checkout function
 * associated with the cart using the currently selected
 * items as the payload for the action.
 * @param {IRetailerLocation} location - the target {@link IRetailerLocation}
 * @param {() => void} callbackFunction - OPTIONAL - callback function to execute after checkout
 */
const emptyLocation = {
    id: '',
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    distance: '',
};
const checkout = (location = emptyLocation, impactRadius, chain = '') => {
    if (state.type === CartType.DEFAULT) {
        console.warn('WARNING: cart type set as DEFAULT');
        return;
    }
    if (state.type === CartType.LANDING_PAGE ||
        state.type === CartType.QS_PARAMS) {
        if (!state.checkoutUrl) {
            console.warn('WARNING: checkout url missing for cart');
            return;
        }
        const url = state.type === CartType.LANDING_PAGE
            ? state.checkoutUrl
            : buildQSParamsUrl();
        window.open(url, '_blank');
        return;
    }
    const selectedItems = getCurrentSelectedItems();
    if (selectedItems.length === 0) {
        console.warn('WARNING: no selected items in cart');
        return;
    }
    if (state.retailer === RetailerPartnerType.NONE ||
        !checkoutServices[state.retailer]) {
        console.warn(`WARNING: checkout function for retailer ${state.retailer} missing`);
        return;
    }
    // Assemble unified Cart API checkout URL
    const baseUrl = `${getApiHost()}${cartEndpoint}`;
    const cartCheckoutUrl = new URL(baseUrl);
    cartCheckoutUrl.searchParams.append('retailer', state.retailer);
    if (state.retailer === RetailerPartnerType.KROGER && chain)
        cartCheckoutUrl.searchParams.append('chain', chain);
    if (location && location.id)
        cartCheckoutUrl.searchParams.append('storeId', location.id);
    cartCheckoutUrl.searchParams.append('items', selectedItems.map((item) => item.id + ':1').join(','));
    if (impactRadius) {
        cartCheckoutUrl.searchParams.append('subId1', impactRadius?.subId1 || '');
        cartCheckoutUrl.searchParams.append('subId2', impactRadius?.subId2 || '');
        cartCheckoutUrl.searchParams.append('subId3', impactRadius?.subId3 || '');
        if (impactRadius.sharedId) {
            cartCheckoutUrl.searchParams.append('sharedId', impactRadius?.sharedId);
        }
    }
    const gamRedirectUrl = getPotentialGAMClickTracker();
    let destinationUrl = cartCheckoutUrl.toString();
    if (window.location.hostname === 'localhost' && gamRedirectUrl) {
        // URI needs to be encoded on localHost ONLY for testing purposes
        destinationUrl = encodeURIComponent(destinationUrl);
    }
    const retailerCheckoutUrl = gamRedirectUrl ? `${gamRedirectUrl}${destinationUrl}` : destinationUrl;
    // console.log('retailerCheckoutUrl', retailerCheckoutUrl);
    // Open checkout URL in new tab
    window.open(retailerCheckoutUrl, '_blank');
};
const getAllItemCollectionsFromCartSections = () => {
    if (!state.carts[state.currentCart]) {
        console.log("cart not found in data-retailers checkout cart store");
        return [];
    }
    const cartSections = state.carts[state.currentCart].cartSections;
    const processItemCollections = [];
    for (const cartSection of cartSections) {
        processItemCollections.push(...cartSection.itemCollections);
    }
    return processItemCollections;
};
const getCurrentSelectedItems = () => {
    const allItemCollections = getAllItemCollectionsFromCartSections();
    let selectedItems = [];
    if (state.carts[state.currentCart] && allItemCollections.length > 0) {
        const selected = allItemCollections.filter((collection) => collection.isSelected);
        selectedItems = selected.map((collection) => collection.items[collection.currentItem]);
    }
    return selectedItems;
};
const isCurrentItemSelected = (cartSectionIndex, cartItemCollectionItemIndex) => {
    if (cartItemCollectionItemIndex > state.carts[state.currentCart].cartSections[cartSectionIndex].itemCollections.length - 1) {
        console.warn(`WARNING: index ${cartItemCollectionItemIndex} out of range for current cart collections`);
        return false;
    }
    const collection = state.carts[state.currentCart].cartSections[cartSectionIndex].itemCollections[cartItemCollectionItemIndex];
    return collection.isSelected;
};
const { set, subscribe, update } = store;
export default {
    set,
    subscribe,
    update,
    init,
    setValue,
    toggleIsItemSelected,
    changeItemForProduct,
    checkout,
    getCurrentSelectedItems,
    isCurrentItemSelected
};
